import { Button, Loader, Select, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useMemo, useState } from 'react';

import {
  useRetrieveAutomationEmailIdentity,
  useUpdateAutomationEmailIdentity,
} from '@/hooks/api/integrations/useAutomationEmailIdentity.ts';
import useRetrieveAutomation from '@/hooks/api/automations/useRetrieveAutomation.ts';
import useRetrieveAutomationVersion from '@/hooks/api/automationVersions/useRetrieveAutomationVersion.ts';
import { StepType } from '@/types/automationFields.ts';
import { TriggerType } from '@/types/automationSteps.ts';

const AutomationEmailIdentityPanel = () => {
  const { data: identity, isLoading, isError } = useRetrieveAutomationEmailIdentity();
  const { data: automation } = useRetrieveAutomation();
  const { data: version } = useRetrieveAutomationVersion(automation?.published_version?.id);
  const { mutate: updateIdentity, isPending } = useUpdateAutomationEmailIdentity();
  const [displayName, setDisplayName] = useState('');
  const [replyToStepId, setReplyToStepId] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (identity && !isDirty) {
      setDisplayName(identity.display_name_override);
      setReplyToStepId(identity.reply_to_step_id);
    }
  }, [identity, isDirty]);

  const replyOptions = useMemo(
    () => [
      { value: '', label: 'Do not route replies into this automation' },
      ...((version?.steps || [])
        .filter(
          (step) =>
            step.step_type === StepType.Trigger &&
            step.trigger_type === TriggerType.External &&
            step.integration_config.integration_type === 'email' &&
            step.integration_config.action_type === 'email_trigger',
        )
        .map((step) => ({ value: step.step_id, label: step.name }))),
    ],
    [version?.steps],
  );

  if (isLoading) return <Loader size="sm" />;
  if (isError || !identity) return <Text c="red" size="sm">Could not load email settings.</Text>;

  const saveIdentity = () => {
    updateIdentity(
      { display_name_override: displayName, reply_to_step_id: replyToStepId },
      {
        onSuccess: (updatedIdentity) => {
          setDisplayName(updatedIdentity.display_name_override);
          setReplyToStepId(updatedIdentity.reply_to_step_id);
          setIsDirty(false);
          notifications.show({ message: 'Email settings saved', color: 'blue' });
        },
        onError: () => notifications.show({ message: 'Could not save email settings', color: 'red' }),
      },
    );
  };

  return (
    <Stack gap="xs">
      <Text size="sm">Emails sent by this automation use this address:</Text>
      <Text size="sm" fw={600}>{identity.email_address}</Text>
      <TextInput
        label="Sender display name"
        placeholder="Defaults to the automation name"
        value={displayName}
        onChange={(event) => {
          setDisplayName(event.currentTarget.value);
          setIsDirty(true);
        }}
        maxLength={255}
        disabled={isPending}
      />
      <Select
        label="Reply handler"
        data={replyOptions}
        value={replyToStepId}
        onChange={(value) => {
          setReplyToStepId(value || '');
          setIsDirty(true);
        }}
        disabled={isPending}
      />
      <Text size="xs" c="dimmed">
        Replies only start a production automation when an Email Trigger is selected and published. Staging test
        replies are not routed into staging.
      </Text>
      <Button
        size="xs"
        loading={isPending}
        onClick={saveIdentity}
      >
        Save email settings
      </Button>
    </Stack>
  );
};

export default AutomationEmailIdentityPanel;
